import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import AdminApiTable from "@/modules/racket_lab_admin/AdminApiTable";
import DispatchStatusBanner from "@/modules/racket_lab_admin/DispatchStatusBanner";
import useAdminApi from "@/hooks/useAdminApi";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLAdminMockOutbox — Sub-sprint C.
 *
 * Read-only inspector for `notifications_mock_outbox`. Every row is a
 * captured envelope. NO send happens here. NO retention purge UI
 * (deferred to Sub-sprint D).
 */
const RLAdminMockOutbox = () => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const [filterTemplate, setFilterTemplate] = useState("");
  const [filterChannel, setFilterChannel] = useState("");
  const params = {};
  if (filterTemplate) params.template_key = filterTemplate;
  if (filterChannel) params.channel = filterChannel;

  const { data, loading, error } = useAdminApi(
    "/admin/notifications/_ops/mock-outbox",
    { params },
  );
  const rows = data?.items || [];

  const columns = [
    { key: "id", label: t("common.columns.id"), render: (r) => (<code className="font-mono text-[10px]">{r.id?.slice(0, 12)}</code>) },
    { key: "template_key", label: t("notifications.mock_outbox.columns.template"), render: (r) => (<span className="text-xs">{r.template_key || "—"}</span>) },
    { key: "channel", label: t("notifications.mock_outbox.columns.channel"), render: (r) => (<span className="text-xs">{r.channel || "—"}</span>) },
    { key: "envelope_id", label: t("notifications.mock_outbox.columns.envelope"), render: (r) => (<code className="text-[10px] text-slate-500">{(r.envelope_id || "").slice(0, 12)}</code>) },
    { key: "result", label: t("notifications.mock_outbox.columns.result"), render: (r) => (
      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${r.result === "captured" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>
        {r.result || "—"}
      </span>
    ) },
    { key: "captured_at", label: t("notifications.mock_outbox.columns.captured_at"), render: (r) => (<span className="text-[10px] text-slate-500">{r.captured_at?.slice(0, 19).replace("T", " ") || "—"}</span>) },
  ];

  return (
    <div data-testid="rl-admin-mock-outbox-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />
      <DispatchStatusBanner />

      <header>
        <h1 className="text-xl font-semibold text-slate-900">{t("notifications.mock_outbox.page_header")}</h1>
        <p className="text-xs text-slate-500">{t("notifications.mock_outbox.subtitle")}</p>
      </header>

      <div data-testid="rl-mock-outbox-filters" className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("notifications.mock_outbox.columns.template")}</span>
          <input
            data-testid="rl-mock-outbox-filter-template"
            value={filterTemplate}
            onChange={(e) => setFilterTemplate(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm font-mono"
            placeholder={t("notifications.mock_outbox.filter_template_placeholder")}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-600">{t("notifications.mock_outbox.columns.channel")}</span>
          <select
            data-testid="rl-mock-outbox-filter-channel"
            value={filterChannel}
            onChange={(e) => setFilterChannel(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">{t("common.filters.any_option")}</option>
            <option value="email">email</option>
            <option value="in_app">in_app</option>
            <option value="dashboard_alert">dashboard_alert</option>
          </select>
        </label>
        <span className="ml-auto text-[10px] text-slate-500">{t("common.list.counter_items", { count: rows.length })}</span>
      </div>

      <AdminApiTable
        columns={columns}
        rows={rows}
        loading={loading}
        error={error}
        emptyLabel={t("notifications.mock_outbox.empty")}
        onRowClick={(r) => navigate(`/admin/racket-lab/notifications/mock-outbox/${r.id}`)}
        dataTestId="rl-mock-outbox-table"
      />
    </div>
  );
};

export default RLAdminMockOutbox;
